import { getPlatform } from '../infrastructure/platform';
import { MESSAGE_TYPES } from '../constants/background';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { storage } from '../utils/storage';
import { syncPremiumFromFirestore } from '../services/premiumSyncStripe';

/**
 * Web replacement for the responsibilities the extension's background worker
 * serves (ADR-012). There is no background context on the web, so the premium
 * reconcile triggers run in-page:
 *
 *  - BILLING_REFRESH bus messages (sent by AuthContext's billing-doc
 *    onSnapshot the moment the Stripe webhook lands),
 *  - app start,
 *  - tab becoming visible again,
 *  - an hourly interval (the extension uses a chrome.alarm — OPS-002 is an
 *    MV3 constraint; a plain interval is correct for a regular page).
 *
 * `syncPremiumFromFirestore` stays the sole writer of premium_status
 * (PREM-001); it reads the billing doc over the Firestore REST API using the
 * credentials AuthContext mirrors into storage, which works identically here.
 * Library data sync needs no equivalent: ApiSyncManager already runs in the
 * UI tree on every surface, including this one.
 */

const PREMIUM_REFRESH_INTERVAL_MS = 60 * 60 * 1000;

/**
 * The reconcile deliberately skips its write when no settings wrapper exists
 * (premiumReconcile.ts) — on the extension, the background's
 * ensureSettingsExist establishes it before any sync runs. The web app has
 * no background, so the same invariant is established here; without it a
 * premium sign-in on a fresh browser never persists (and the upgrade gate
 * never lifts). Mirrors background.ts ensureSettingsExist.
 */
async function ensureSettingsExist(): Promise<void> {
  const storedSettings = await storage.get(STORAGE_KEYS.SETTINGS);
  if (!storedSettings) {
    await storage.set(STORAGE_KEYS.SETTINGS, {
      premiumExpiryAttempts: 0,
      premiumLastChecked: Date.now(),
      settings: {
        theme: 'light',
        premium_status: 'free',
        features: {},
        updated_at: new Date().toISOString(),
        last_online_sync: new Date().toISOString(),
      },
    });
  }
}

let started = false;

export function startWebRuntime(): void {
  if (started) return;
  started = true;

  const platform = getPlatform();

  platform.bus.subscribe(message => {
    if (message?.type === MESSAGE_TYPES.BILLING_REFRESH) {
      void syncPremiumFromFirestore('web-billing-refresh').catch(() => {});
    }
  });

  // Durable sign-in trigger. BILLING_REFRESH (from the auth snapshot) can
  // fire BEFORE the batched billingAuth write lands, making the reconcile
  // read empty credentials and bail — and then nothing retries until the
  // tab is hidden/shown or the hourly tick. Reconciling when the
  // credentials actually appear in storage closes that race (the extension
  // is covered by its many background wake-ups; the web needs this).
  platform.storage.onChanged(changes => {
    if (changes[STORAGE_KEYS.BILLING_AUTH]?.newValue) {
      void syncPremiumFromFirestore('web-billing-auth').catch(() => {});
    }
  });

  void ensureSettingsExist()
    .then(() => syncPremiumFromFirestore('web-start'))
    .catch(() => {});

  setInterval(() => {
    void syncPremiumFromFirestore('web-interval').catch(() => {});
  }, PREMIUM_REFRESH_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void syncPremiumFromFirestore('web-visible').catch(() => {});
    }
  });
}
